import React, { useState, useRef, useEffect, ReactElement } from "react";
import { cn } from "@/lib/utils";

export enum InputType {
  INPUT = "input",
  TEXTAREA = "textarea",
}

interface EditableWrapperProps {
  value: string;
  children: ReactElement;
  className?: string;
  onEdit: (newValue: string) => void;
  type?: InputType;
  disabled?: boolean;

  // If in linear mode, always editable and no outlines
  linearMode?: boolean;
}

const EditableWrapper: React.FC<EditableWrapperProps> = ({
  children,
  onEdit,
  value,
  className = "",
  type = InputType.INPUT,
  disabled = false,
  linearMode = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const handleBlur = () => {
    setIsEditing(false);
    onEdit(tempValue);
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      handleBlur();
    }
  };

  const adjustTextAreaHeight = (target: HTMLTextAreaElement) => {
    target.style.height = "auto"; // Reset the height first
    target.style.height = `${target.scrollHeight}px`; // Set the height based on content
  };

  useEffect(() => {
    if (
      isEditing &&
      type === InputType.TEXTAREA &&
      inputRef.current &&
      inputRef.current instanceof HTMLTextAreaElement
    ) {
      adjustTextAreaHeight(inputRef.current);
    }
  }, [isEditing, type, tempValue]);

  const childClassName = React.Children.only(children).props.className;

  if (isEditing || linearMode) {
    if (type === InputType.TEXTAREA) {
      return (
        <textarea
          ref={inputRef as React.MutableRefObject<HTMLTextAreaElement>}
          className={cn(
            "bg-transparent outline-1 focus:border-none focus:outline focus:outline-primary-0 w-full border-none p-0 resize-none outline-none outline-offset-0",
            linearMode &&
              "outline-none focus:outline-none outline-[transparent] focus:outline-[transparent]",
            className,
            childClassName
          )}
          value={tempValue}
          onChange={(e) => {
            const { target } = e;
            adjustTextAreaHeight(target);
            setTempValue(target.value);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyPress}
          rows={1}
          style={{
            height: `${(tempValue.match(/\n/g) || []).length * 20 + 20}px`,
          }}
        />
      );
    } else {
      return (
        <input
          ref={inputRef as React.MutableRefObject<HTMLInputElement>}
          className={cn(
            "bg-transparent w-full outline-1 focus:border-none focus:outline focus:outline-primary-0 border-none p-0 outline-none outline-offset-0",
            linearMode &&
              "outline-none focus:outline-none outline-[transparent] focus:outline-[transparent]",
            className,
            childClassName
          )}
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyPress}
        />
      );
    }
  }

  // Wrap the child with a div that has the onClick handler.
  return (
    <div
      className={cn(
        "hover:outline-secondary-600 hover:outline w-full",
        linearMode && "hover:outline-none hover:outline-[transparent]",
        className,
        childClassName
      )}
      onClick={() => !disabled && setIsEditing(true)}
    >
      {children}
    </div>
  );
};

export default React.memo(EditableWrapper);
